// Role of this file:
// Listen for an action
// produce a side effect which is communicate with the server via http,
// bring data back and dispatch a new action (success or fail)
// Actions is an observable that listens to the types of actions
// being dispatched and we can respond accordingly

// Angular Stuff
import * as fromRoot from "#core/router-store";
import * as LoginStore from "#login/store";
import * as loginFormActions from "#login/store/actions/login-form.action";
import { LocalStorage } from "#services/local-storage.service";
import { SnackBarService } from "#services/snackbar.service";
import { Inject, Injectable } from "@angular/core";
// rxjs
import { Actions, createEffect, ofType } from "@ngrx/effects";
// Store Stuff
import { select, Store } from "@ngrx/store";
// Services
import { UsherPayloadHelper } from "#core/helpers/usher-payload.helper";
import * as StudentsActions from "#core/teacher-classes/store/actions/students.action";
import { Router } from "@angular/router";
import { USER_SERVICE } from "@carnegie-learning/data-access-angular-fido";
import { CoursePagination, CoursesService, ProfileService, SchoolsService, TeachersService, WelcomeContentService } from "@carnegie-learning/data-access-angular-passport";
import { UserService } from "@carnegie-learning/data-access-fido";
import { Course } from "cli-types";
import { Observable, of } from "rxjs";
import {
    catchError,
    map,
    switchMap,
    tap,
    withLatestFrom,
} from "rxjs/operators";
import * as coursesActions from "../actions/teacher-classes.action";

@Injectable()
export class CoursesEffects {
    constructor(
        private actions$: Actions,
        private schoolsService: SchoolsService,
        private coursesService: CoursesService,
        private snackBarService: SnackBarService,
        private profileService: ProfileService,
        private localStorage: LocalStorage,
        private store: Store<fromRoot.CustomSerializer>,
        private loginStore: Store<LoginStore.UserState>,
        private welcomeContentService: WelcomeContentService,
        private teachersService: TeachersService,
        @Inject(USER_SERVICE) private userService: UserService,
        private router: Router,
        private usherPayloadHelper: UsherPayloadHelper
    ) {}

    loadDropdownCourses$ = createEffect(() =>
        this.actions$
            .pipe(ofType(coursesActions.loadCoursesDropdown))
            // produce a side effect
            .pipe(
                map((action) => action),
                switchMap((action) => {
                    return this.coursesService.getUnpagedCourses().pipe(
                        map((response) => {
                            return coursesActions.loadCoursesDropdownSuccess({
                                payload: response,
                            });
                        }),
                        // need to wrap the error in 'of' so it becomes an observable action
                        catchError((error) =>
                            of(
                                coursesActions.loadCoursesDropdownFail({
                                    payload: error,
                                })
                            )
                        )
                    );
                })
            )
    );

    loadDropdownCoursesSuccess$ = createEffect(() =>
        this.actions$
            .pipe(ofType(coursesActions.loadCoursesDropdownSuccess))
            .pipe(
                map((action) => action),
                switchMap((action) => {
                    const studentIDs: string[] = [];
                    // Use a map to avoid duplicates
                    const studentIDMap = new Map<string, boolean>();
                    action.payload.data.forEach((course: Course) => {
                        if (course.students && course.students.length > 0) {
                            course.students.forEach((student) => {
                                studentIDMap.set(student.userGUID, true);
                            });
                        }
                    });
                    // convert map to array since values would be unique
                    studentIDMap.forEach((value, key) => {
                        studentIDs.push(key);
                    });

                    return this.userService.getUsers(studentIDs).pipe(
                        map((fidoUserData) =>
                            coursesActions.loadCoursesFidoStudentsDropdownSuccess(
                                {
                                    courses: action.payload.data,
                                    fidoData: fidoUserData,
                                }
                            )
                        ),
                        catchError((error) =>
                            of(
                                coursesActions.loadCoursesFidoStudentsDropdownFail(
                                    { payload: error }
                                )
                            )
                        )
                    );
                })
            )
    );

    loadCourses$ = createEffect(() =>
        // this is where we listen for the action type
        this.actions$
            .pipe(ofType(coursesActions.loadCourses))
            // produce a side effect
            .pipe(
                map((action) => action.payload),
                withLatestFrom(
                    this.store.pipe(select(fromRoot.getRouterState)),
                    (action, router) => {
                        if (router) {
                            return {
                                params: action,
                                schoolID: router.state.params.schoolID,
                                teacherID: router.state.params.teacherID
                            };
                        } else {
                            return {
                                params: action,
                                schoolID: null,
                                teacherID: null,
                            };
                        }
                    }
                ),
                switchMap((latestPayload) => {
                    let promise: Observable<CoursePagination> | null = null;
                    if (latestPayload.teacherID) {
                        promise = this.teachersService.getTeacherCourses(
                            latestPayload.params,
                            latestPayload.teacherID
                        );
                    } else if (latestPayload.schoolID) {
                        promise = this.schoolsService.getSchoolCourses(
                            latestPayload.params,
                            latestPayload.schoolID
                        );
                    } else {
                        // Generic getCourses is deprecated and we want to switch to explicitly calling teacher/student
                        promise = this.coursesService.getCourses(
                            latestPayload.params
                        );
                    }
                    return promise.pipe(
                        // mapping over all the teacher-classes and returning an action
                        map((courses) => courses),
                        map((response) =>
                            coursesActions.loadCoursesSuccess({
                                payload: response,
                            })
                        ),
                        // need to wrap the error in 'of' so it becomes an observable action
                        catchError((error) =>
                            of(
                                coursesActions.loadCoursesFail({
                                    payload: error,
                                })
                            )
                        )
                    );
                })
            )
    );

    loadCourse$ = createEffect(() =>
        this.actions$
            .pipe(ofType(coursesActions.loadCourse))
            // produce a side effect
            .pipe(
                map((action) => action.courseID),
                switchMap((courseID) => {
                    return this.coursesService.getCourse(courseID).pipe(
                        // mapping over all the teacher-classes and returning an action
                        map((course) => course),
                        map((response) =>
                            coursesActions.loadCourseSuccess({
                                payload: response,
                            })
                        ),
                        // need to wrap the error in 'of' so it becomes an observable action
                        catchError((error) =>
                            of(
                                coursesActions.loadCourseFail({
                                    payload: error,
                                })
                            )
                        )
                    );
                })
            )
    );
    createCourse$ = createEffect(() =>
        this.actions$.pipe(ofType(coursesActions.createCourse)).pipe(
            map((action) => action),
            switchMap((action) => {
                return this.coursesService.createCourse(action.payload).pipe(
                    map((newCourse) => {
                        this.snackBarService.success("Class Created!");
                        return coursesActions.createCourseSuccess({
                            payload: newCourse,
                        });
                    }),
                    catchError((error) =>
                        of(coursesActions.createCourseFail({ payload: error }))
                    )
                );
            })
        )
    );

    updateCourse$ = createEffect(() =>
        this.actions$.pipe(ofType(coursesActions.updateCourse)).pipe(
            map((action) => action),
            switchMap((action) => {
                return this.coursesService.updateCourse(action.payload).pipe(
                    map((course) => {
                        // Clear params so up to date data is fetched in Content Library
                        this.localStorage.remove('teacherResourcesParams');
                        this.snackBarService.success("Class Updated!");
                        return coursesActions.updateCourseSuccess({
                            payload: course,
                        });
                    }),
                    catchError((error) =>
                        of(coursesActions.updateCourseFail({ payload: error }))
                    )
                );
            })
        )
    );

    setCourseGrid$ = createEffect(() =>
        this.actions$.pipe(ofType(coursesActions.setCourseGrid)).pipe(
            map((action) => action),
            switchMap((action) => {
                return this.coursesService
                    .setFgGridID(action.courseID, action.gridID)
                    .pipe(
                        map((course) => {
                            this.snackBarService.success("Course Updated!");
                            return coursesActions.updateCourseSuccess({
                                payload: course,
                            });
                        }),
                        catchError((error) =>
                            of(
                                coursesActions.updateCourseFail({
                                    payload: error,
                                })
                            )
                        )
                    );
            })
        )
    );

    regenCourseGrid$ = createEffect(() =>
        this.actions$.pipe(ofType(coursesActions.regenCourseGrid)).pipe(
            map((action) => action),
            switchMap((action) => {
                return this.coursesService.regenFgGridID(action.courseID).pipe(
                    map((course) => {
                        this.snackBarService.success("Course Updated!");
                        return coursesActions.updateCourseSuccess({
                            payload: course,
                        });
                    }),
                    catchError((error) =>
                        of(coursesActions.updateCourseFail({ payload: error }))
                    )
                );
            })
        )
    );

    updateRosterCoursesBulk$ = createEffect(() =>
        this.actions$.pipe(ofType(coursesActions.updateRosterCoursesBulk)).pipe(
            map((action) => action),
            switchMap((action) => {
                return this.coursesService.updateCourses(action.payload).pipe(
                    map((courses) => {
                        this.snackBarService.success("Course Updated!");
                        return coursesActions.updateRosterCoursesBulkSuccess({
                            payload: courses,
                        });
                    }),
                    catchError((error) => {
                        this.snackBarService.success(
                            "Failed To Update Language(s) For Course(s)!"
                        );
                        return of(
                            coursesActions.updateRosterCoursesBulkFail({
                                payload: error,
                            })
                        );
                    })
                );
            })
        )
    );

    updateRosterCoursesBulkSuccess$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(coursesActions.updateRosterCoursesBulkSuccess),
                tap(() => this.router.navigate(["/teacher/classes"]))
            ),
        { dispatch: false }
    );

    handleCourseCreateSuccess$ = createEffect(() =>
        // Routing effect for successful create
        this.actions$
            .pipe(
                ofType(
                    coursesActions.createCourseSuccess,
                    coursesActions.updateCourseSuccess
                )
            )
            .pipe(
                withLatestFrom(
                    this.store.pipe(select(fromRoot.getRouterState)),
                    (action, router) => {
                        if (router) {
                            let createCourseSuccess = false;
                            if (action.type.includes("Create")) {
                                createCourseSuccess = true;
                            }
                            return {
                                districtID: router.state.params.districtID,
                                schoolID: router.state.params.schoolID,
                                teacherID: router.state.params.teacherID,
                                courseID: action.payload.courseID,
                                createCourseSuccess: createCourseSuccess,
                            };
                        } else {
                            return {
                                districtID: null,
                                schoolID: null,
                                teacherID: null,
                                courseID: null,
                            };
                        }
                    }
                ),
                map((routerPayload) => {
                    if (routerPayload.districtID) {
                        if (routerPayload.teacherID) {
                            return new fromRoot.Go({
                                path: [
                                    `distributor/districts/${routerPayload.districtID}/schools/${routerPayload.schoolID}/teachers/${routerPayload.teacherID}/courses`,
                                ],
                            });
                        } else {
                            return new fromRoot.Go({
                                path: [
                                    `distributor/districts/${routerPayload.districtID}/schools/${routerPayload.schoolID}/courses`,
                                ],
                            });
                        }
                    } else {
                        let path = `teacher/classes/${routerPayload.courseID}/students`;
                        if (routerPayload.createCourseSuccess) {
                            path = path + "/new";
                        }
                        return new fromRoot.Go({
                            path: [path],
                        });
                    }
                })
            )
    );

    archiveCourse$ = createEffect(() =>
        this.actions$.pipe(ofType(coursesActions.archiveCourse)).pipe(
            map((action) => action.payload),
            switchMap((course) => {
                return this.coursesService.archiveCourse(course).pipe(
                    map((archivedCourse) => {
                        this.snackBarService.success("Class Archived!");
                        this.localStorage.remove("selectedCourse");
                        this.store.dispatch(StudentsActions.removeStudents());
                        return coursesActions.archiveCourseSuccess({
                            payload: archivedCourse,
                        });
                    }),
                    catchError((error) =>
                        of(coursesActions.archiveCourseFail({ payload: error }))
                    )
                );
            })
        )
    );

    reactivateCourse$ = createEffect(() =>
        this.actions$.pipe(ofType(coursesActions.reactivateCourse)).pipe(
            map((action) => action.payload),
            switchMap((course) => {
                return this.coursesService.reactivateCourse(course).pipe(
                    map((reactivatedCourse) => {
                        this.snackBarService.success("Class Reactivated!");
                        return coursesActions.reactivateCourseSuccess({
                            payload: reactivatedCourse,
                        });
                    }),
                    catchError((error) =>
                        of(
                            coursesActions.reactivateCourseFail({
                                payload: error,
                            })
                        )
                    )
                );
            })
        )
    );

    removeCourse$ = createEffect(() =>
        this.actions$.pipe(ofType(coursesActions.removeCourse)).pipe(
            map((action) => action.payload),
            switchMap((course) => {
                return this.coursesService.removeCourse(course).pipe(
                    map(() => {
                        this.snackBarService.success("Class Deleted!");
                        this.localStorage.remove("selectedCourse");
                        return coursesActions.removeCourseSuccess({
                            payload: course,
                        });
                    }),
                    catchError((error) =>
                        of(coursesActions.removeCourseFail({ payload: error }))
                    )
                );
            })
        )
    );

    setSelectCourse$ = createEffect(() =>
        this.actions$.pipe(ofType(coursesActions.setSelectedCourse)).pipe(
            map((action) => action.payload),
            switchMap((course) => {
                const userData = this.localStorage.get("userProfile");

                let courseID: number | null = null;
                if (course) {
                    courseID = course.courseID;
                    this.localStorage.set("selectedCourse", course);
                    this.usherPayloadHelper.setClassData(course);
                }

                if (courseID === userData.lastCourse) {
                    // No changes! Skip the API hit and go straight to the routing.
                    return of(
                        coursesActions.updateSelectedCourseSuccess({ courseID })
                    );
                }

                const payload = { lastCourse: courseID };
                return this.profileService.setUserSelectedCourse(payload).pipe(
                    map(() => {
                        return coursesActions.updateSelectedCourseSuccess({
                            courseID,
                        });
                    }),
                    catchError((error) =>
                        of(coursesActions.updateSelectedCourseFail())
                    )
                );
            })
        )
    );

    resetCourse$ = createEffect(() =>
        this.actions$.pipe(
            ofType(loginFormActions.userLoginSuccess),
            map((_) => {
                this.loginStore.dispatch(
                    LoginStore.setLastCourse({ courseID: null })
                );
                const userData = this.localStorage.get("userProfile");
                userData.lastCourse = null;
                this.localStorage.set("userProfile", userData);
                return coursesActions.updateSelectedCourseSuccess({
                    courseID: null,
                });
            })
        )
    );

    updateSelectedCourseSuccess$ = createEffect(() =>
        this.actions$
            .pipe(ofType(coursesActions.updateSelectedCourseSuccess))
            .pipe(
                map((action) => {
                    if (action.courseID === null || !action.courseID) {
                        this.loginStore.dispatch(
                            LoginStore.setLastCourse({ courseID: null })
                        );
                        let userData = this.localStorage.get("userProfile");
                        userData = { ...userData, lastCourse: null };
                        this.localStorage.set("userProfile", userData);
                        return coursesActions.selectedCourseNoAction();
                    } else {
                        this.loginStore.dispatch(
                            LoginStore.setLastCourse({
                                courseID: action.courseID,
                            })
                        );
                        let userData = this.localStorage.get("userProfile");
                        userData = { ...userData, lastCourse: action.courseID };
                        this.localStorage.set("userProfile", userData);
                        return coursesActions.selectedCourseNoAction();
                    }
                })
            )
    );

    loadSelectedCourse$ = createEffect(() =>
        this.actions$
            .pipe(ofType(coursesActions.loadSelectedCourse))
            // produce a side effect
            .pipe(
                map((action) => action.courseID),
                switchMap((courseID) => {
                    return this.coursesService.getCourse(courseID).pipe(
                        // teacher-class primaryTeacher attempts to get fido Profile
                        switchMap((course) => {
                            const teacherId = course.primaryTeacher.userGUID;
                            return this.userService.getUsers([teacherId]).pipe(
                                map((users) => {
                                    const user = users[0];
                                    let updatedCourse: Course = {
                                        ...course,
                                        primaryTeacher: {
                                            ...course.primaryTeacher,
                                            firstName: user?.givenName,
                                            lastName: user?.surName,
                                            eAddress: user?.email,
                                        },
                                    };

                                    return updatedCourse;
                                })
                            );
                        }),
                        // mapping over all the teacher-classes and returning an action
                        map((response) => {
                            this.localStorage.set("selectedCourse", response);
                            return coursesActions.loadSelectedCourseSuccess({
                                course: response,
                            });
                        }),
                        // need to wrap the error in 'of' so it becomes an observable action
                        catchError((error) =>
                            of(coursesActions.loadSelectedCourseFail())
                        )
                    );
                })
            )
    );

    loadCoursesBackground$ = createEffect(() =>
        this.actions$.pipe(ofType(coursesActions.loadCoursesBackground)).pipe(
            map((action) => action),
            switchMap((payload) => {
                return this.welcomeContentService
                    .getWelcomeContent(payload.subjectId, payload.timeOfDay)
                    .pipe(
                        map((backgroundImg) =>
                            coursesActions.loadCoursesBackgroundSuccess({
                                payload: backgroundImg,
                            })
                        ),
                        catchError((error) =>
                            of(
                                coursesActions.loadCoursesBackgroundFail({
                                    payload: error,
                                })
                            )
                        )
                    );
            })
        )
    );

    // Commenting out for PAS-11616
    // loadBackgroundImage$ = createEffect(() =>
    //     this.actions$.pipe(ofType(coursesActions.loadBackgroundImage)).pipe(
    //         map((action) => action),
    //         switchMap((payload) => {
    //             return this.welcomeContentService
    //                 .getWelcomeContent(payload.subjectId, payload.timeOfDay)
    //                 .pipe(
    //                     map((backgroundImg) =>
    //                         coursesActions.loadBackgroundImageSuccess({
    //                             payload: backgroundImg,
    //                         })
    //                     ),
    //                     catchError((error) =>
    //                         of(
    //                             coursesActions.loadBackgroundImageFail({
    //                                 payload: error,
    //                             })
    //                         )
    //                     )
    //                 );
    //         })
    //     )
    // );

    loadMetrics$ = createEffect(() =>
        this.actions$.pipe(ofType(coursesActions.loadCourseMetrics)).pipe(
            map((action) => action.courseID),
            switchMap((courseID) => {
                return this.coursesService.getCourseMetrics(courseID).pipe(
                    map((courseMetrics) =>
                        coursesActions.loadCourseMetricsSuccess({
                            courseID,
                            courseMetrics,
                        })
                    ),
                    catchError((error) =>
                        of(
                            coursesActions.loadCourseMetricsFail({
                                payload: error,
                            })
                        )
                    )
                );
            })
        )
    );
}
